import Layout from '@/shared/components/layout/Layout';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, useMemo, useLayoutEffect } from 'react';
import { PageSkeleton } from '@/shared/components/ui/PageSkeleton';
import { ProtectedRoute } from '@/shared/components/guards';
import { publicRoutes, forbiddenRoute, notFoundRoute } from './routes';
import { getProtectedRoutes } from './dynamicRoutes';
import { usePermissionStore, useUIStore } from '@/store';
import { getTitleForFunction } from './componentRegistry';

interface AppRouterProps {
  isAuthenticated: boolean;
}

/**
 * Application routing component
 *
 * Dynamic Route System:
 * - Public routes: Static (login, SSO)
 * - Protected routes: Generated dynamically from menuList (backend API)
 * - Routes auto-update when permissions change
 *
 * Permission Checking:
 * - ProtectedRoute handles permission validation
 * - Uses functionId from route meta or auto-detects from path
 * - Unauthorized access redirects to /403
 */
function AppRouter({ isAuthenticated }: AppRouterProps) {
  const location = useLocation();
  const { menuList } = usePermissionStore();
  const setCurrentFunction = useUIStore(state => state.setCurrentFunction);

  // Generate protected routes dynamically from menuList
  const protectedRoutes = useMemo(() => {
    if (!isAuthenticated) {
      return [];
    }
    return getProtectedRoutes(menuList);
  }, [isAuthenticated, menuList]);

  // Auto-update header title when route changes
  // Using useLayoutEffect to update before paint (prevent flash)
  useLayoutEffect(() => {
    if (!isAuthenticated) {
      setCurrentFunction('');
      return;
    }

    // Wait for routes to be ready
    if (protectedRoutes.length === 0) {
      return;
    }

    const currentRoute = protectedRoutes.find(route => {
      const routePath = route.index ? '/' : `/${route.path}`;
      return routePath === location.pathname;
    });

    if (currentRoute?.meta?.functionId) {
      const title = getTitleForFunction(currentRoute.meta.functionId);
      setCurrentFunction(title || '');
    } else {
      // Route not found in registry - show empty
      setCurrentFunction('');
    }
  }, [location.pathname, protectedRoutes, isAuthenticated, setCurrentFunction]);

  return (
    <Routes>
      {/* Public routes - redirect to home if already authenticated */}
      {publicRoutes.map(({ path, component: Component }) => (
        <Route
          key={path}
          path={path}
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Component />
          }
        />
      ))}

      {/* Protected routes - require authentication */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Layout />
          ) : (
            <Navigate to="/login" replace state={{ from: location.pathname }} />
          )
        }
      >
        {protectedRoutes.map(({ path, component: Component, index, meta }) => {
          // Dashboard always accessible, no permission check needed
          const isDashboard = index || path === '/';

          return (
            <Route
              key={path}
              index={index}
              path={index ? undefined : path}
              element={
                isDashboard ? (
                  <Suspense fallback={<PageSkeleton showTable />}>
                    <Component />
                  </Suspense>
                ) : (
                  <ProtectedRoute
                    functionId={meta?.functionId}
                    // If no functionId in meta, ProtectedRoute will auto-detect from path
                  >
                    <Suspense fallback={<PageSkeleton showTable />}>
                      <Component />
                    </Suspense>
                  </ProtectedRoute>
                )
              }
            />
          );
        })}
      </Route>

      {/* 403 Forbidden route */}
      <Route
        path={forbiddenRoute.path}
        element={<forbiddenRoute.component />}
      />

      {/* Catch-all route for 404 Not Found */}
      {/* If not authenticated, redirect to login instead of showing 404 */}
      <Route
        path={notFoundRoute.path}
        element={
          isAuthenticated ? (
            <notFoundRoute.component />
          ) : (
            <Navigate to="/login" replace state={{ from: location.pathname }} />
          )
        }
      />
    </Routes>
  );
}

export default AppRouter;
